import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createClub } from '../services/clubs';
import { canCreateClub } from '../services/licenses';
import { useAuth } from '../contexts/AuthContext';
import './CreateClub.css';

const CreateClub: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isProfileComplete } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionCheck, setPermissionCheck] = useState<{
    canCreate: boolean;
    reason?: string;
  } | null>(null);

  useEffect(() => {
    const checkPermission = async () => {
      try {
        const permission = await canCreateClub();
        setPermissionCheck(permission);
        
        if (!permission.canCreate) {
          setError(permission.reason || t('licenses.upgradeRequired'));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to check permissions');
      }
    };
    
    checkPermission();
  }, [t]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError(t('clubs.errors.nameRequired'));
      return;
    }

    // Check permissions before creating
    if (!permissionCheck?.canCreate) {
      setError(permissionCheck?.reason || t('licenses.upgradeRequired'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const club = await createClub({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
      });
      
      // Navigate to the new club's management page
      navigate(`/clubs/${club.id}/manage`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('clubs.errors.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/clubs');
  };

  // Check if profile is complete
  if (!isProfileComplete) {
    return (
      <div className="create-club-container">
        <div className="create-club-header">
          <h1>{t('clubs.createClub')}</h1>
        </div>
        <div className="profile-incomplete-warning">
          <div className="warning-icon">⚠️</div>
          <h3>{t('clubs.profileIncompleteTitle')}</h3>
          <p>{t('clubs.profileIncompleteMessage')}</p>
          <button 
            onClick={() => navigate('/profile')} 
            className="btn btn-primary"
          >
            {t('clubs.completeProfile')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="create-club-container">
      <div className="create-club-header">
        <h1>{t('clubs.createClub')}</h1>
        <p className="create-club-subtitle">
          {t('clubs.createClubDescription')}
        </p>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="create-club-form">
        <div className="form-group">
          <label htmlFor="name" className="form-label">
            {t('clubs.clubName')} *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="form-input"
            placeholder={t('clubs.clubNamePlaceholder')}
            required
            disabled={loading || !permissionCheck?.canCreate}
            maxLength={100}
          />
          <div className="form-help">
            {t('clubs.clubNameHelp')}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description" className="form-label">
            {t('clubs.description')}
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="form-textarea"
            placeholder={t('clubs.descriptionPlaceholder')}
            disabled={loading}
            rows={4}
            maxLength={500}
          />
          <div className="form-help">
            {t('clubs.descriptionHelp')}
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !formData.name.trim() || !permissionCheck?.canCreate}
          >
            {loading ? t('clubs.creating') : t('clubs.createClub')}
          </button>
        </div>
      </form>

      <div className="create-club-info">
        <h3>{t('clubs.afterCreation')}</h3>
        <ul>
          <li>{t('clubs.afterCreation1')}</li>
          <li>{t('clubs.afterCreation2')}</li>
          <li>{t('clubs.afterCreation3')}</li>
        </ul>
      </div>
    </div>
  );
};

export default CreateClub;
